<template>
  <v-container fluid>
    <v-layout align-center class="mb-4">
      <v-icon large color="primary" class="mr-2">scale</v-icon>
      <div>
        <h2 class="title primary--text font-weight-bold">Cola de Pesajes de Salida</h2>
        <div class="caption grey--text">Registra el pesaje antes de autorizar la salida en caseta</div>
      </div>
      <v-spacer></v-spacer>
      <v-btn icon :loading="loading" @click="fetchQueue" title="Actualizar">
        <v-icon>refresh</v-icon>
      </v-btn>
    </v-layout>

    <v-layout row wrap class="mb-4">
      <v-flex xs12 sm6 md3 class="pa-1">
        <v-card class="pa-3 text-xs-center elevation-1">
          <div class="display-1 font-weight-black primary--text">{{ queue.total || 0 }}</div>
          <div class="caption grey--text text-uppercase">Total embarques hoy</div>
        </v-card>
      </v-flex>
      <v-flex xs12 sm6 md3 class="pa-1">
        <v-card class="pa-3 text-xs-center elevation-1" style="border-left: 4px solid #FF9800;">
          <div class="display-1 font-weight-black orange--text darken-2">{{ queue.pending_weigh_out_count || 0 }}</div>
          <div class="caption grey--text text-uppercase">Pendientes de pesaje salida</div>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card class="elevation-2">
      <v-toolbar flat color="primary" dark dense>
        <v-icon class="mr-2">list</v-icon>
        <v-toolbar-title class="subheading">Embarques del día</v-toolbar-title>
      </v-toolbar>
      <v-data-table
        :headers="headers"
        :items="allItems"
        :loading="loading"
        class="elevation-0"
        no-data-text="No hay embarques registrados hoy"
      >
        <template v-slot:items="props">
          <td>{{ props.item.driver_name || '—' }}</td>
          <td>{{ props.item.vehicle_plate || '—' }}</td>
          <td>{{ props.item.vehicle_type || '—' }}</td>
          <td>
            <v-chip x-small :color="props.item.has_weigh_in ? 'success' : 'grey'" outline>
              {{ props.item.has_weigh_in
                ? (props.item.weight_in_kg != null ? props.item.weight_in_kg.toLocaleString('es-MX') + ' kg' : 'Registrado')
                : 'Pendiente' }}
            </v-chip>
          </td>
          <td>
            <v-chip
              x-small
              :color="props.item.has_weigh_out ? 'success' : (props.item.requires_weigh_out ? 'warning' : 'grey')"
              outline
            >
              {{ props.item.has_weigh_out
                ? (props.item.weight_out_kg != null ? props.item.weight_out_kg.toLocaleString('es-MX') + ' kg' : 'Registrado')
                : (props.item.requires_weigh_out ? 'Pendiente' : 'N/A') }}
            </v-chip>
          </td>
          <td>
            <v-btn
              v-if="props.item.requires_weigh_out && !props.item.has_weigh_out"
              x-small
              color="warning"
              @click="startWeighOut(props.item)"
            >
              <v-icon x-small left>scale</v-icon>
              Pesar
            </v-btn>
            <v-icon v-else-if="props.item.has_weigh_out" color="success" small>check_circle</v-icon>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogWeighOut" persistent max-width="900px">
      <ScaleWeighing
        v-if="dialogWeighOut && selectedItem"
        :modo="'SALIDA'"
        :info-invitation="selectedItemAsInvitation"
        :pre-filled-data="selectedItemPreFilled"
        @save="onWeighOutSave"
      />
      <v-btn
        v-if="dialogWeighOut"
        fab
        small
        color="white"
        style="position:fixed; top:16px; right:16px; z-index:9999;"
        @click="dialogWeighOut = false"
      >
        <v-icon color="grey darken-2">close</v-icon>
      </v-btn>
    </v-dialog>

    <v-dialog v-model="dialogSuccess" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark color="success">
          <v-icon large class="mr-3">check_circle</v-icon>
          <v-toolbar-title class="headline font-weight-bold">Pesaje registrado</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-4 text-xs-center">
          <template v-if="weighOutResult">
            <div class="subheading mb-3 font-weight-bold">Resumen de pesos</div>
            <v-layout row wrap justify-center class="mb-3">
              <v-flex xs4 class="text-xs-center">
                <div class="caption grey--text text-uppercase">Peso entrada</div>
                <div class="title font-weight-bold primary--text">
                  {{ weighOutResult.weight_in_kg != null ? weighOutResult.weight_in_kg.toLocaleString('es-MX') : '—' }} kg
                </div>
              </v-flex>
              <v-flex xs4 class="text-xs-center">
                <div class="caption grey--text text-uppercase">Peso salida</div>
                <div class="title font-weight-bold orange--text">
                  {{ weighOutResult.weight_out_kg != null ? weighOutResult.weight_out_kg.toLocaleString('es-MX') : '—' }} kg
                </div>
              </v-flex>
              <v-flex xs4 class="text-xs-center">
                <div class="caption grey--text text-uppercase">Peso neto</div>
                <div class="title font-weight-bold success--text">
                  {{ weighOutResult.weight_net_kg != null ? weighOutResult.weight_net_kg.toLocaleString('es-MX') : '—' }} kg
                </div>
              </v-flex>
            </v-layout>
            <v-divider class="mb-3"></v-divider>
            <v-chip color="success" outline>
              <v-icon left small>check</v-icon>
              {{ weighOutResult.cav_ready ? 'CAV listo para imprimir' : 'Pesaje registrado correctamente' }}
            </v-chip>
          </template>
          <div class="body-1 mt-3 grey--text">
            Ahora procede al escáner de salida en caseta para marcar la salida del embarque.
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 justify-center">
          <v-btn large color="primary" @click="goToExitScan">
            <v-icon left>qr_code_scanner</v-icon>
            Ir a escáner de salida
          </v-btn>
          <v-btn flat @click="dialogSuccess = false; fetchQueue()">
            <v-icon left>list</v-icon>
            Ver cola
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="4000">
      {{ snackbarMessage }}
      <v-btn flat @click="snackbar = false">Cerrar</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import ScaleWeighing from '@/views/ScaleWeighing.vue'

export default {
  name: 'ShipmentWeighQueue',
  components: { ScaleWeighing },
  data() {
    return {
      loading: false,
      loadingWeighOut: false,
      queue: {},
      dialogWeighOut: false,
      dialogSuccess: false,
      selectedItem: null,
      weighOutResult: null,
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      headers: [
        { text: 'Conductor', value: 'driver_name' },
        { text: 'Placa', value: 'vehicle_plate' },
        { text: 'Tipo vehículo', value: 'vehicle_type' },
        { text: 'Peso entrada', value: 'weight_in_kg', sortable: false },
        { text: 'Peso salida', value: 'weight_out_kg', sortable: false },
        { text: 'Acción', sortable: false }
      ]
    }
  },
  computed: {
    allItems() {
      return this.queue.items || []
    },
    selectedItemAsInvitation() {
      if (!this.selectedItem) return {}
      return {
        guest_first_name: this.selectedItem.driver_name || '',
        guest_last_name: '',
        vehicle_plate: this.selectedItem.vehicle_plate || '',
        transport_type: this.selectedItem.vehicle_type || '',
        client_name: '',
        observations: ''
      }
    },
    selectedItemPreFilled() {
      if (!this.selectedItem) return {}
      return {
        driver_name: this.selectedItem.driver_name || '',
        vehicle_plate: this.selectedItem.vehicle_plate || '',
        vehicle_type: this.selectedItem.vehicle_type || ''
      }
    }
  },
  created() {
    this.fetchQueue()
  },
  methods: {
    fetchQueue() {
      this.loading = true
      axios({
        method: 'get',
        url: 'https://empresa-service.intrare.app/shipments/scale/guard/queue/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      })
        .then(response => {
          this.loading = false
          this.queue = response.data
        })
        .catch(error => {
          this.loading = false
          this.showSnackbar('Error al cargar la cola de pesajes', 'error')
          console.error(error)
        })
    },

    startWeighOut(item) {
      this.selectedItem = item
      this.dialogWeighOut = true
    },

    onWeighOutSave(weighData) {
      if (!this.selectedItem) return
      this.loadingWeighOut = true
      axios({
        method: 'post',
        url: 'https://empresa-service.intrare.app/shipments/scale/weigh-out/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        },
        data: {
          shipping_window_id: this.selectedItem.shipping_window_id,
          weight_kg: weighData.weight_kg,
          scale_number: weighData.scaleNumber,
          guard_notes: ''
        }
      })
        .then(response => {
          this.loadingWeighOut = false
          this.weighOutResult = response.data
          this.dialogWeighOut = false
          this.dialogSuccess = true
        })
        .catch(error => {
          this.loadingWeighOut = false
          const responseData = error.response && error.response.data
          // Si ya existe una pesada de salida, tratar como éxito y proceder
          if (responseData && (responseData.code === 'WEIGH_OUT_EXISTS' || responseData.code === 'DUPLICATE_WEIGH_OUT')) {
            this.dialogWeighOut = false
            this.showSnackbar('Ya existe una pesada de salida registrada para este embarque.', 'info')
            this.fetchQueue()
            return
          }
          const errorMsg = responseData && responseData.error
            ? responseData.error
            : 'Error al registrar pesada de salida'
          this.showSnackbar(errorMsg, 'error')
          console.error(error)
        })
    },

    goToExitScan() {
      this.dialogSuccess = false
      this.$router.push({ name: 'access', params: { tab: 'shipments' } })
    },

    showSnackbar(message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color || 'error'
      this.snackbar = true
    }
  }
}
</script>
